import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, Grid, Card, CardContent, Button, useMediaQuery, useTheme } from '@mui/material';
import { psiTokens, lightButtonSx } from '../theme/psiTheme';
import { usePageMeta } from '../hooks/usePageMeta';

const methodologies = [
  { code: 'QRQC', name: 'Quick Response Quality Control', description: 'Fast, on-the-floor problem resolution focused on immediate containment and root cause.' },
  { code: 'A3', name: 'A3 Thinking', description: 'A structured, one-page format for framing problems, analysis, and countermeasures.' },
  { code: 'PDCA', name: 'Plan-Do-Check-Act', description: 'The core cycle behind continuous improvement, applied iteratively to any process.' },
  { code: '8D', name: 'Eight Disciplines', description: 'A team-based methodology for identifying, correcting, and eliminating recurring problems.' },
  { code: 'FMEA', name: 'Failure Mode & Effects Analysis', description: 'Proactive risk assessment to prioritize and prevent potential failure modes.' },
  { code: 'Kata', name: 'Toyota Kata', description: 'A coaching routine that builds the daily habit of scientific thinking in teams.' },
];

export default function LearnMore() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  usePageMeta(
    'Learn More',
    'The structured problem-solving methodologies behind PSI’s results: QRQC, A3, PDCA, 8D, FMEA, and Toyota Kata, taught with real-project mentorship.'
  );

  return (
    <Box sx={{ backgroundColor: psiTokens.background.tertiary }}>
      {/* Page header */}
      <Box sx={{ position: 'relative', py: isMobile ? 6 : 10 }}>
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '4px',
            height: '100%',
            background: `linear-gradient(180deg, ${psiTokens.primary.dark}, ${psiTokens.primary.light})`,
          }}
        />
        <Container maxWidth="xl">
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: psiTokens.primary.light,
              mb: 2,
            }}
          >
            Learn More
          </Typography>
          <Typography variant="h2" sx={{ color: psiTokens.text.primary, fontWeight: 700, mb: 3, maxWidth: '780px' }}>
            The methodologies behind the results.
          </Typography>
          <Typography
            sx={{
              fontSize: 'clamp(16px, 2.2vw, 18px)',
              color: psiTokens.text.tertiary,
              lineHeight: 1.7,
              maxWidth: '680px',
            }}
          >
            PSI trains teams in the structured problem-solving frameworks used by leading manufacturers
            worldwide. Each methodology below is taught with real-project mentorship, not just theory.
          </Typography>
        </Container>
      </Box>

      {/* Methodologies grid */}
      <Box sx={{ backgroundColor: psiTokens.background.primary, py: isMobile ? 6 : 8 }}>
        <Container maxWidth="xl">
          <Box
            sx={{
              width: '40px',
              height: '3px',
              background: `linear-gradient(90deg, ${psiTokens.primary.dark}, ${psiTokens.primary.light})`,
              borderRadius: '2px',
              mb: 2,
            }}
          />
          <Typography variant="h3" sx={{ color: psiTokens.text.primary, fontWeight: 700, mb: 5 }}>
            Structured problem-solving frameworks.
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            {methodologies.map((m, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <Card
                  sx={{
                    height: '100%',
                    backgroundColor: psiTokens.background.secondary,
                    borderRadius: '12px',
                    border: `1px solid ${psiTokens.border}`,
                    borderTop: `4px solid ${psiTokens.primary.dark}`,
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      sx={{
                        fontSize: '11px',
                        fontWeight: 700,
                        letterSpacing: '0.1em',
                        color: psiTokens.primary.light,
                        mb: 1,
                      }}
                    >
                      {m.code}
                    </Typography>
                    <Typography variant="h6" sx={{ color: psiTokens.text.primary, fontWeight: 700, mb: 1.5, fontSize: '15px' }}>
                      {m.name}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: psiTokens.text.secondary, lineHeight: 1.7 }}>
                      {m.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ backgroundColor: psiTokens.background.secondary, py: isMobile ? 6 : 8 }}>
        <Container maxWidth="xl">
          <Box
            sx={{
              background: `linear-gradient(135deg, ${psiTokens.primary.dark} 0%, #7b3d92 50%, ${psiTokens.primary.light} 100%)`,
              borderRadius: '16px',
              p: isMobile ? 3 : 4,
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: isMobile ? '18px' : '20px', fontWeight: 700, color: '#ffffff', mb: 0.5 }}>
                Want a deeper walkthrough of PSI's methodology?
              </Typography>
              <Typography sx={{ fontSize: '14px', color: 'rgba(255,255,255,0.75)' }}>
                Get in touch and we'll tailor the conversation to your team's challenges.
              </Typography>
            </Box>
            <Button
              component={RouterLink}
              to="/contact"
              variant="contained"
              sx={{
                ...lightButtonSx,
                fontSize: '13px',
                fontWeight: 700,
                textTransform: 'uppercase',
                px: 3,
                py: 1.5,
                flexShrink: 0,
              }}
            >
              Contact Us
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
